/**
 * FilterChipsIsland - Shows active filter chips below the Command Island
 *
 * Only renders when filters are active. Each chip shows the preset name
 * with an × to remove. Includes a filter/highlight mode toggle and "Clear All."
 */

import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Island } from './Island'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useRampFilterStore, FILTER_PRESETS } from '@/stores/rampFilterStore'

interface FilterChipsIslandProps {
  minimized?: boolean
}

export function FilterChipsIsland({ minimized }: FilterChipsIslandProps) {
  const {
    activePresets,
    displayMode,
    highlightedAircraftIds,
    togglePreset,
    clearAllPresets,
    setDisplayMode,
    clearHighlights,
  } = useRampFilterStore()

  const hasPresets = activePresets.size > 0
  const hasHighlights = highlightedAircraftIds.size > 0

  // Don't render if nothing is active
  if (!hasPresets && !hasHighlights) return null

  const activePresetConfigs = FILTER_PRESETS.filter((p) => activePresets.has(p.key))

  return (
    <Island
      minimized={minimized}
      className="absolute top-[72px] left-1/2 -translate-x-1/2 z-20 px-3 py-2"
    >
      <div className="flex items-center gap-2">
        {/* Active preset chips */}
        {activePresetConfigs.map((preset) => (
          <Badge
            key={preset.key}
            variant="secondary"
            className="gap-1 pr-1 cursor-default"
          >
            {preset.label}
            <button
              className="ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20 transition-colors"
              onClick={() => togglePreset(preset.key)}
              title={`Remove ${preset.label} filter`}
            >
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}

        {/* Highlight chip (from search) */}
        {hasHighlights && (
          <Badge variant="secondary" className="gap-1 pr-1 cursor-default">
            {highlightedAircraftIds.size} highlighted
            <button
              className="ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20 transition-colors"
              onClick={clearHighlights}
              title="Clear highlights"
            >
              <X className="w-3 h-3" />
            </button>
          </Badge>
        )}

        {/* Separator */}
        {hasPresets && (
          <>
            <div className="w-px h-4 bg-border" />

            {/* Display mode toggle */}
            <div className="flex rounded-md border border-border overflow-hidden text-xs">
              <button
                className={cn(
                  'px-2 py-1 transition-colors',
                  displayMode === 'filter'
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-accent'
                )}
                onClick={() => setDisplayMode('filter')}
                title="Hide non-matching aircraft"
              >
                Filter
              </button>
              <button
                className={cn(
                  'px-2 py-1 transition-colors',
                  displayMode === 'highlight'
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-accent'
                )}
                onClick={() => setDisplayMode('highlight')}
                title="Dim non-matching aircraft"
              >
                Highlight
              </button>
            </div>

            {/* Clear all */}
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={clearAllPresets}
            >
              Clear
            </Button>
          </>
        )}
      </div>
    </Island>
  )
}
